/**
 * Clutch Reel compositor (browser only): plays the fan's own recording through the selected
 * segments, paints the broadcast package on a canvas, and records the canvas (plus the
 * recording's audio when the browser allows) with MediaRecorder → a vertical webm.
 * Geometry and copy come from layout.ts / segments.ts (pure, unit-tested).
 */
import { formatClock, team, type GameEvent, type GameMeta, type Side } from '@clutch/core';
import { canvasSafeUrl, headshotUrl, nameInitials, teamLogoUrl } from '../media/assets';
import { teamAccent } from '../ui/color';
import { pickRecorderMime } from '../studio/recorder';
import { coverCrop, fitFont, reelLayout, type ReelLayout, type Rect } from './layout';
import { beatAt, reelCaption, type ReelSegment } from './segments';

export type ReelSource = { url: string; durationMs: number; name: string };

export type ReelInput = {
  mine: ReelSource;
  friend?: ReelSource; // side-by-side when set (segments must carry friendStartMs)
  segments: (ReelSegment & { friendStartMs?: number })[];
  plays: ReadonlyMap<number, GameEvent>;
  meta: GameMeta;
  callLine?: string;
  final?: { away: number; home: number };
  onProgress?: (p: number) => void;
  signal?: AbortSignal;
};

export type ReelResult = { blob: Blob; url: string; mimeType: string; durationMs: number };

const INTRO_MS = 1300;
const OUTRO_MS = 1800;
const LIME = '#d4ff3a';
const INK = '#0b0b0d';
const BONE = '#f5f3ee';
const DISPLAY = '"Big Shoulders Display", "Arial Narrow", sans-serif';
const BODY = '"Barlow", "Helvetica Neue", Arial, sans-serif';
const MONO = '"IBM Plex Mono", ui-monospace, monospace';

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

function once(el: EventTarget, ev: string, timeoutMs: number): Promise<boolean> {
  return new Promise((resolve) => {
    const t = setTimeout(() => {
      el.removeEventListener(ev, on);
      resolve(false);
    }, timeoutMs);
    const on = () => {
      clearTimeout(t);
      el.removeEventListener(ev, on);
      resolve(true);
    };
    el.addEventListener(ev, on);
  });
}

export function loadImage(url: string | undefined, timeoutMs = 4000): Promise<HTMLImageElement | undefined> {
  if (!url) return Promise.resolve(undefined);
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    const finish = (loaded: boolean) => {
      clearTimeout(t);
      img.onload = null;
      img.onerror = null;
      // Resolving a fallback alone leaves the request occupying a connection.
      // Release it so the fan's saved recording can load on the same origin.
      if (!loaded) img.removeAttribute('src');
      resolve(loaded ? img : undefined);
    };
    const t = setTimeout(() => finish(false), timeoutMs);
    img.onload = () => finish(img.naturalWidth > 0);
    img.onerror = () => finish(false);
    img.src = url;
  });
}

/** A recording's <video>, with MediaRecorder's missing duration fixed so seeking works. */
async function prepareVideo(url: string): Promise<HTMLVideoElement> {
  const v = document.createElement('video');
  v.playsInline = true;
  v.preload = 'auto';
  v.crossOrigin = 'anonymous';
  v.src = url;
  if (v.readyState < 1) await once(v, 'loadedmetadata', 5000);
  if (v.readyState < 1 || v.error) {
    v.removeAttribute('src');
    v.load();
    throw new Error('Recording did not load in time');
  }
  if (!Number.isFinite(v.duration)) {
    v.currentTime = 1e7; // forces Chromium to scan a MediaRecorder webm and learn its duration
    await once(v, 'seeked', 3000);
  }
  return v;
}

/** Browser play promises can remain pending on a stalled source. Never cut forever. */
async function playVideo(v: HTMLVideoElement, signal?: AbortSignal): Promise<void> {
  signal?.throwIfAborted();
  let timer: ReturnType<typeof setTimeout> | undefined;
  let onAbort: (() => void) | undefined;
  try {
    await Promise.race([
      v.play(),
      new Promise<never>((_resolve, reject) => {
        timer = setTimeout(() => reject(new Error('Recording playback stalled')), 8000);
        onAbort = () => reject(signal?.reason ?? new DOMException('Cancelled', 'AbortError'));
        signal?.addEventListener('abort', onAbort, { once: true });
      }),
    ]);
  } finally {
    clearTimeout(timer);
    if (onAbort) signal?.removeEventListener('abort', onAbort);
  }
}

async function seek(v: HTMLVideoElement, ms: number): Promise<void> {
  const t = Math.max(0, ms / 1000);
  if (Math.abs(v.currentTime - t) < 0.02) return;
  v.currentTime = t;
  await once(v, 'seeked', 2500);
}

function roundRect(ctx: CanvasRenderingContext2D, r: Rect, radius: number) {
  ctx.beginPath();
  ctx.roundRect(r.x, r.y, r.w, r.h, radius);
}

function drawVideo(ctx: CanvasRenderingContext2D, v: HTMLVideoElement | undefined, box: Rect, zoom: number) {
  ctx.save();
  ctx.beginPath();
  ctx.rect(box.x, box.y, box.w, box.h);
  ctx.clip();
  ctx.fillStyle = '#16161b';
  ctx.fillRect(box.x, box.y, box.w, box.h);
  if (v && v.videoWidth > 0) {
    const crop = coverCrop(v.videoWidth, v.videoHeight, box);
    const zw = box.w * zoom;
    const zh = box.h * zoom;
    // Not mirrored: the reel shows the fan the way their friends see them (text reads right).
    ctx.translate(box.x + box.w / 2, box.y + box.h / 2);
    ctx.drawImage(v, crop.x, crop.y, crop.w, crop.h, -zw / 2, -zh / 2, zw, zh);
  }
  ctx.restore();
  // Broadcast grade: vignette top and bottom so the package reads on any background.
  const g = ctx.createLinearGradient(0, box.y, 0, box.y + box.h);
  g.addColorStop(0, 'rgba(11,11,13,0.55)');
  g.addColorStop(0.22, 'rgba(11,11,13,0)');
  g.addColorStop(0.62, 'rgba(11,11,13,0)');
  g.addColorStop(1, 'rgba(11,11,13,0.78)');
  ctx.fillStyle = g;
  ctx.fillRect(box.x, box.y, box.w, box.h);
}

function drawCircleImage(ctx: CanvasRenderingContext2D, img: HTMLImageElement | undefined, r: Rect, ring: string, fallback: string) {
  const cx = r.x + r.w / 2;
  const cy = r.y + r.h / 2;
  const rad = r.w / 2;
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, rad, 0, Math.PI * 2);
  ctx.fillStyle = ring;
  ctx.fill();
  ctx.beginPath();
  ctx.arc(cx, cy, rad - 5, 0, Math.PI * 2);
  ctx.fillStyle = '#1b1b21';
  ctx.fill();
  ctx.clip();
  if (img) {
    // NBA headshots are 1040×760 with the face in the upper-middle: crop a square around it.
    const s = Math.min(img.naturalWidth, img.naturalHeight);
    const sx = (img.naturalWidth - s) / 2;
    ctx.drawImage(img, sx, 0, s, s, r.x + 5, r.y + 12, r.w - 10, r.h - 10);
  } else {
    ctx.fillStyle = BONE;
    ctx.font = `900 ${Math.round(r.w * 0.38)}px ${DISPLAY}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(fallback, cx, cy + 2);
  }
  ctx.restore();
}

function drawLogo(ctx: CanvasRenderingContext2D, img: HTMLImageElement | undefined, x: number, y: number, size: number, code: string) {
  if (img) {
    ctx.drawImage(img, x, y, size, size);
    return;
  }
  ctx.fillStyle = teamAccent(code);
  ctx.beginPath();
  ctx.arc(x + size / 2, y + size / 2, size / 2, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = BONE;
  ctx.font = `900 ${Math.round(size * 0.36)}px ${DISPLAY}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(code, x + size / 2, y + size / 2 + 1);
}

type Assets = { logos: Record<Side, HTMLImageElement | undefined>; heads: Map<number, HTMLImageElement | undefined> };

function drawScoreBug(ctx: CanvasRenderingContext2D, L: ReelLayout, meta: GameMeta, e: GameEvent | undefined, a: Assets) {
  const r = L.scoreBug;
  ctx.save();
  roundRect(ctx, r, 18);
  ctx.fillStyle = 'rgba(11,11,13,0.82)';
  ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,0.08)';
  ctx.lineWidth = 2;
  ctx.stroke();
  const half = (r.w - 150) / 2;
  const side = (sd: Side, x: number) => {
    const code = sd === 'home' ? meta.home.tricode : meta.away.tricode;
    const score = e ? (sd === 'home' ? e.homeScore : e.awayScore) : sd === 'home' ? meta.finalHome : meta.finalAway;
    const other = e ? (sd === 'home' ? e.awayScore : e.homeScore) : sd === 'home' ? meta.finalAway : meta.finalHome;
    ctx.fillStyle = teamAccent(code);
    ctx.fillRect(x, r.y + 14, 6, r.h - 28);
    drawLogo(ctx, a.logos[sd], x + 18, r.y + 14, r.h - 28, code);
    ctx.fillStyle = score >= other ? BONE : 'rgba(245,243,238,0.6)';
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'left';
    ctx.font = `800 30px ${DISPLAY}`;
    ctx.fillText(code, x + r.h - 2, r.y + r.h / 2 + 1);
    ctx.textAlign = 'right';
    ctx.font = `900 54px ${DISPLAY}`;
    ctx.fillText(String(score), x + half - 16, r.y + r.h / 2 + 3);
  };
  side('away', r.x + 10);
  side('home', r.x + 10 + half);
  const clock = e ? formatClock(e.period, e.clockSec) : 'FINAL';
  const [per, time] = clock.split(' ');
  const cx = r.x + r.w - 75;
  ctx.textAlign = 'center';
  ctx.fillStyle = LIME;
  ctx.font = `600 20px ${MONO}`;
  ctx.fillText(per ?? '', cx, r.y + 28);
  ctx.fillStyle = BONE;
  ctx.font = `900 34px ${DISPLAY}`;
  ctx.fillText(time ?? '', cx, r.y + 60);
  ctx.restore();
}

function drawLowerThird(ctx: CanvasRenderingContext2D, L: ReelLayout, e: GameEvent, meta: GameMeta, a: Assets, appear: number) {
  if (appear <= 0) return;
  const lt = L.lowerThird;
  const slide = (1 - easeOut(Math.min(1, appear))) * -60;
  ctx.save();
  ctx.globalAlpha = Math.min(1, appear * 1.6);
  ctx.translate(slide, 0);
  const sideCode = e.side === 'home' ? meta.home.tricode : e.side === 'away' ? meta.away.tricode : undefined;
  const accent = sideCode ? teamAccent(sideCode) : LIME;
  const box = { x: lt.x + 70, y: lt.y, w: lt.w - 70, h: lt.h };
  roundRect(ctx, box, 16);
  ctx.fillStyle = 'rgba(11,11,13,0.9)';
  ctx.fill();
  ctx.fillStyle = accent;
  ctx.fillRect(box.x, box.y + box.h - 8, box.w * Math.min(1, appear), 8);
  const [what, score, clock] = reelCaption(e).split(' · ');
  const textX = box.x + 120;
  const maxW = box.x + box.w - textX - 20;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = BONE;
  const size = fitFont((s) => ((ctx.font = `900 ${s}px ${DISPLAY}`), ctx.measureText(what ?? '').width), maxW, 64, 30);
  ctx.font = `900 ${size}px ${DISPLAY}`;
  ctx.fillText(what ?? '', textX, box.y + 66);
  ctx.font = `500 24px ${MONO}`;
  ctx.fillStyle = 'rgba(245,243,238,0.78)';
  ctx.fillText(`${meta.away.tricode} ${score?.replace('–', ` – `)} ${meta.home.tricode} · ${clock}`, textX, box.y + 106);
  const head = e.personId ? a.heads.get(e.personId) : undefined;
  drawCircleImage(ctx, head, L.headshot, accent, nameInitials(e.playerName));
  ctx.restore();
}

function drawFooter(ctx: CanvasRenderingContext2D, L: ReelLayout, callLine: string | undefined) {
  ctx.save();
  if (callLine) {
    const c = L.callChip;
    ctx.font = `700 26px ${BODY}`;
    const w = Math.min(c.w, ctx.measureText(callLine).width + 56);
    roundRect(ctx, { x: c.x, y: c.y, w, h: c.h }, c.h / 2);
    ctx.fillStyle = LIME;
    ctx.fill();
    ctx.fillStyle = INK;
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'left';
    ctx.fillText(callLine, c.x + 28, c.y + c.h / 2 + 1, c.w - 56);
  }
  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'left';
  ctx.fillStyle = LIME;
  ctx.font = `900 ${L.wordmark.size}px ${DISPLAY}`;
  ctx.letterSpacing = '6px';
  ctx.fillText('CLUTCH', L.wordmark.x, L.wordmark.y + 12);
  ctx.letterSpacing = '0px';
  ctx.fillStyle = 'rgba(245,243,238,0.7)';
  ctx.font = `500 20px ${MONO}`;
  ctx.textAlign = 'right';
  ctx.fillText('REEL', L.w - L.wordmark.x, L.wordmark.y + 10);
  ctx.restore();
}

function drawPaneLabel(ctx: CanvasRenderingContext2D, r: Rect, label: string, me: boolean) {
  ctx.save();
  ctx.font = `700 24px ${BODY}`;
  const w = ctx.measureText(label).width + 40;
  // Top pane: under the score bug. Bottom pane: near its bottom edge, clear of the seam's lower third.
  const y = r.y === 0 ? r.y + 150 : r.y + 96;
  roundRect(ctx, { x: L_PAD, y, w, h: 42 }, 21);
  ctx.fillStyle = me ? LIME : 'rgba(11,11,13,0.8)';
  ctx.fill();
  ctx.fillStyle = me ? INK : BONE;
  ctx.textBaseline = 'middle';
  ctx.fillText(label, L_PAD + 20, y + 22);
  ctx.restore();
}
const L_PAD = 32;

function easeOut(t: number) {
  return 1 - Math.pow(1 - t, 3);
}

function drawCard(ctx: CanvasRenderingContext2D, L: ReelLayout, meta: GameMeta, a: Assets, t: number, kind: 'intro' | 'outro', input: ReelInput) {
  const awayC = teamAccent(meta.away.tricode);
  const homeC = teamAccent(meta.home.tricode);
  ctx.save();
  ctx.fillStyle = INK;
  ctx.fillRect(0, 0, L.w, L.h);
  // Diagonal team split, sliding in.
  const k = easeOut(Math.min(1, t * 1.8));
  ctx.globalAlpha = 0.9;
  ctx.fillStyle = awayC;
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(L.w * 0.62 * k, 0);
  ctx.lineTo(L.w * 0.38 * k, L.h);
  ctx.lineTo(0, L.h);
  ctx.fill();
  ctx.fillStyle = homeC;
  ctx.beginPath();
  ctx.moveTo(L.w, 0);
  ctx.lineTo(L.w - L.w * 0.38 * k, 0);
  ctx.lineTo(L.w - L.w * 0.62 * k, L.h);
  ctx.lineTo(L.w, L.h);
  ctx.fill();
  ctx.globalAlpha = 1;
  const grad = ctx.createLinearGradient(0, 0, 0, L.h);
  grad.addColorStop(0, 'rgba(11,11,13,0.2)');
  grad.addColorStop(1, 'rgba(11,11,13,0.85)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, L.w, L.h);
  const s = 0.92 + 0.08 * k;
  ctx.translate(L.w / 2, L.h / 2);
  ctx.scale(s, s);
  drawLogo(ctx, a.logos.away, -260, -300, 200, meta.away.tricode);
  drawLogo(ctx, a.logos.home, 60, -300, 200, meta.home.tricode);
  ctx.textAlign = 'center';
  ctx.fillStyle = BONE;
  if (kind === 'intro') {
    ctx.font = `900 120px ${DISPLAY}`;
    ctx.fillText('CLUTCH', 0, 40);
    ctx.fillStyle = LIME;
    ctx.fillText('REEL', 0, 150);
    ctx.fillStyle = BONE;
    ctx.font = `600 30px ${MONO}`;
    ctx.fillText(`${meta.away.tricode} @ ${meta.home.tricode}`, 0, 230);
    ctx.font = `700 34px ${BODY}`;
    const who = input.friend ? `${input.mine.name.toUpperCase()} × ${input.friend.name.toUpperCase()}` : `${input.mine.name.toUpperCase()}’S REACTION`;
    ctx.fillText(who, 0, 290);
  } else {
    const f = input.final;
    ctx.font = `600 30px ${MONO}`;
    ctx.fillStyle = LIME;
    ctx.fillText(f ? 'FINAL' : 'THE MOMENT', 0, -20);
    ctx.fillStyle = BONE;
    ctx.font = `900 150px ${DISPLAY}`;
    if (f) ctx.fillText(`${f.away}–${f.home}`, 0, 130);
    if (input.callLine) {
      ctx.font = `700 32px ${BODY}`;
      ctx.fillStyle = LIME;
      ctx.fillText(input.callLine, 0, 220, L.w - 80);
    }
    ctx.fillStyle = BONE;
    ctx.font = `900 64px ${DISPLAY}`;
    ctx.fillText('CLUTCH', 0, 360);
  }
  ctx.restore();
}

/** Compose the reel. Resolves when the webm is finalized. */
export async function composeReel(input: ReelInput): Promise<ReelResult> {
  const checkAbort = () => input.signal?.throwIfAborted();
  checkAbort();
  const split = !!input.friend;
  const L = reelLayout(split ? 'split' : 'solo', { me: input.mine.name, ...(input.friend ? { friend: input.friend.name } : {}) });
  const canvas = document.createElement('canvas');
  canvas.width = L.w;
  canvas.height = L.h;
  const ctx = canvas.getContext('2d')!;
  try {
    await Promise.all([document.fonts.load(`900 40px ${DISPLAY}`), document.fonts.load(`700 20px ${BODY}`), document.fonts.load(`500 20px ${MONO}`)]);
  } catch {
    /* fonts are a nicety */
  }

  const src = input.meta.source;
  const personIds = [...new Set(input.segments.flatMap((s) => s.seqs.map((q) => input.plays.get(q)?.personId)).filter((x): x is number => !!x))];
  const [awayLogo, homeLogo, ...heads] = await Promise.all([
    loadImage(canvasSafeUrl(teamLogoUrl(input.meta.away))),
    loadImage(canvasSafeUrl(teamLogoUrl(input.meta.home))),
    ...personIds.map((id) => loadImage(canvasSafeUrl(headshotUrl(id, src)))),
  ]);
  const assets: Assets = { logos: { away: awayLogo, home: homeLogo }, heads: new Map(personIds.map((id, i) => [id, heads[i]])) };
  checkAbort();

  const mineV = await prepareVideo(input.mine.url);
  let friendV: HTMLVideoElement | undefined;
  try {
    friendV = input.friend ? await prepareVideo(input.friend.url) : undefined;
  } catch (error) {
    mineV.pause(); mineV.removeAttribute('src'); mineV.load();
    throw error;
  }
  if (input.signal?.aborted) {
    for (const v of [mineV, friendV]) if (v) { v.pause(); v.removeAttribute('src'); v.load(); }
    checkAbort();
  }

  const stream = canvas.captureStream(30);
  // Audio: the fan's own reaction (their voice is the point). Best-effort.
  let audioCtx: AudioContext | undefined;
  try {
    audioCtx = new AudioContext();
    const dest = audioCtx.createMediaStreamDestination();
    audioCtx.createMediaElementSource(mineV).connect(dest);
    if (friendV) {
      const g = audioCtx.createGain();
      g.gain.value = 0.6;
      audioCtx.createMediaElementSource(friendV).connect(g).connect(dest);
    }
    for (const t of dest.stream.getAudioTracks()) stream.addTrack(t);
  } catch {
    mineV.muted = true;
    if (friendV) friendV.muted = true;
  }

  const mime = pickRecorderMime((m) => m.startsWith('video/webm') && MediaRecorder.isTypeSupported(m)) ?? pickRecorderMime();
  const rec = new MediaRecorder(stream, { ...(mime ? { mimeType: mime } : {}), videoBitsPerSecond: 4_000_000 });
  const chunks: Blob[] = [];
  rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
  const stopped = new Promise<void>((r) => (rec.onstop = () => r()));

  const total = INTRO_MS + OUTRO_MS + input.segments.reduce((t, s) => t + (s.endMs - s.startMs), 0);
  let doneMs = 0;
  const progress = () => input.onProgress?.(Math.min(1, doneMs / total));

  // Painter state, read by the frame loop.
  let scene: { kind: 'card'; card: 'intro' | 'outro'; t0: number } | { kind: 'seg'; seg: ReelInput['segments'][number] } = {
    kind: 'card',
    card: 'intro',
    t0: performance.now(),
  };
  const paint = () => {
    if (scene.kind === 'card') {
      drawCard(ctx, L, input.meta, assets, (performance.now() - scene.t0) / 1000, scene.card, input);
      return;
    }
    const seg = scene.seg;
    const nowMs = mineV.currentTime * 1000;
    const beat = beatAt(seg, nowMs) ?? { seq: seg.seq, markMs: seg.markMs };
    const toMark = nowMs - beat.markMs;
    // Punch-in on the play: a quick zoom that peaks as the play lands, then a flash.
    const bump = Math.max(0, 1 - Math.abs(toMark) / 900);
    L.panes.forEach((p, i) => drawVideo(ctx, i === 0 ? mineV : friendV, p.rect, 1 + 0.07 * bump));
    if (toMark >= 0 && toMark < 260) {
      ctx.fillStyle = `rgba(255,255,255,${0.28 * (1 - toMark / 260)})`;
      ctx.fillRect(0, 0, L.w, L.h);
    }
    if (split) L.panes.forEach((p, i) => p.label && drawPaneLabel(ctx, p.rect, p.label, i === 0));
    const e = input.plays.get(beat.seq);
    drawScoreBug(ctx, L, input.meta, e, assets);
    if (e) drawLowerThird(ctx, L, e, input.meta, assets, (toMark + 600) / 500);
    drawFooter(ctx, L, split ? undefined : input.callLine);
  };
  const iv = setInterval(paint, 1000 / 30);
  paint();
  rec.start(250);

  try {
    await wait(INTRO_MS);
    checkAbort();
    doneMs += INTRO_MS;
    progress();
    for (const seg of input.segments) {
      checkAbort();
      await seek(mineV, seg.startMs);
      if (friendV) await seek(friendV, seg.friendStartMs ?? seg.startMs);
      scene = { kind: 'seg', seg };
      const len = seg.endMs - seg.startMs;
      await Promise.all([playVideo(mineV, input.signal), friendV ? playVideo(friendV, input.signal) : undefined]);
      const t0 = performance.now();
      while (performance.now() - t0 < len + 400 && mineV.currentTime * 1000 < seg.endMs && !mineV.ended) {
        checkAbort();
        await wait(30);
      }
      mineV.pause();
      friendV?.pause();
      doneMs += len;
      progress();
    }
    scene = { kind: 'card', card: 'outro', t0: performance.now() };
    await wait(OUTRO_MS);
    checkAbort();
    doneMs = total;
    progress();
  } finally {
    clearInterval(iv);
    rec.stop();
    await stopped;
    stream.getTracks().forEach((t) => t.stop());
    for (const v of [mineV, friendV]) if (v) { v.pause(); v.removeAttribute('src'); v.load(); }
    void audioCtx?.close().catch(() => undefined);
  }
  const type = (rec.mimeType || mime || 'video/webm').split(';')[0]!;
  const blob = new Blob(chunks, { type });
  return { blob, url: URL.createObjectURL(blob), mimeType: type, durationMs: total };
}

/** Share the reel as a file (Web Share level 2), else download it. */
export async function shareReel(blob: Blob, filename: string, text: string): Promise<'shared' | 'downloaded' | 'cancelled'> {
  const file = new File([blob], filename, { type: blob.type || 'video/webm' });
  try {
    if (navigator.canShare?.({ files: [file] })) {
      await navigator.share({ files: [file], title: 'My Clutch Reel', text });
      return 'shared';
    }
  } catch (err) {
    if ((err as Error)?.name === 'AbortError') return 'cancelled';
  }
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 4000);
  return 'downloaded';
}

export const teamNameOf = (code: string) => team(code).name;
